import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchActiveLuckyDraw,
  participateInLuckyDraw,
  resetParticipateSuccess,
} from "../../../Redux/luckydraw/slice";
import { toast } from "react-toastify";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";
import {
  CalendarDays,
  ChevronRight,
  Clock3,
  Search,
  Trophy,
  Users,
  X,
} from "lucide-react";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import {
  formatStuffDate,
  formatStuffLongDate,
  mapLuckyDrawToRecord,
} from "./stuffAdapters";
import { LuckyDraw as LuckyDrawType } from "../../../Redux/luckydraw/types";
import "./LuckyDrawPage.css";

const LuckyDraw = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { activeLuckyDraw, loading, success, message, error } = useSelector(
    (state: RootState) => state.luckyDraw,
  );

  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [timers, setTimers] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    dispatch(fetchActiveLuckyDraw());
  }, [dispatch]);

  useEffect(() => {
    if (success.participate && message) {
      toast.success(message);
      dispatch(resetParticipateSuccess());
      dispatch(fetchActiveLuckyDraw());
    }
    if (error) {
      toast.error(error);
    }
  }, [success.participate, message, error, dispatch]);

  const handleParticipate = (id: string) => {
    dispatch(participateInLuckyDraw(id));
  };

  const calculateTimeLeft = (endDate: string) => {
    const difference = +new Date(endDate) - +new Date();
    let timeLeft: any = {};

    if (difference > 0) {
      timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return timeLeft;
  };

  useEffect(() => {
    const timer = setInterval(() => {
      if (activeLuckyDraw) {
        const newTimers: any = {};
        activeLuckyDraw.forEach((draw) => {
          newTimers[draw._id] = calculateTimeLeft(
            draw.luckyDrawDateRange.endDate,
          );
        });
        setTimers(newTimers);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [activeLuckyDraw]);

  const renderTimer = (drawId: string) => {
    const timeLeft = timers[drawId];
    if (!timeLeft)
      return (
        <span className="lucky-draw-timer-pending">
          <Clock3 size={13} strokeWidth={2} /> Calculating...
        </span>
      );
    if (Object.keys(timeLeft).length === 0)
      return (
        <span className="lucky-draw-timer-pending">
          <Clock3 size={13} strokeWidth={2} /> Expired
        </span>
      );

    return (
      <div className="lucky-draw-countdown">
        {[
          { value: timeLeft.days, label: "Days" },
          { value: timeLeft.hours, label: "Hours" },
          { value: timeLeft.minutes, label: "Mins" },
          { value: timeLeft.seconds, label: "Secs" },
        ].map((part) => (
          <div className="lucky-draw-countdown-cell" key={part.label}>
            <strong>{part.value}</strong>
            <small>{part.label}</small>
          </div>
        ))}
      </div>
    );
  };

  const renderDrawMedia = (draw: LuckyDrawType, className: string) => {
    const media = [...draw.media.images, ...draw.media.videos];
    if (media.length === 0) {
      return (
        <div className={className}>
          <img src="../../assets/img/icons/awards.png" alt={draw.name} />
        </div>
      );
    }
    return (
      <div className={`${className} lucky-draw-media-carousel`}>
        <Carousel
          showThumbs={false}
          infiniteLoop
          autoPlay
          interval={3000}
          showStatus={false}
          showIndicators={media.length > 1}
        >
          {media.map((src, idx) => (
            <div key={idx}>
              <img src={src} alt={`${draw.name} ${idx + 1}`} loading="lazy" />
            </div>
          ))}
        </Carousel>
      </div>
    );
  };

  const filteredDraws = (activeLuckyDraw || []).filter((draw) => {
    const term = searchValue.trim().toLowerCase();
    const matchesSearch =
      !term || draw.name?.toLowerCase().includes(term);
    const start = draw.luckyDrawDateRange?.startDate || "";
    const matchesFrom = !dateRange.from || start >= dateRange.from;
    const matchesTo = !dateRange.to || start <= dateRange.to;
    return matchesSearch && matchesFrom && matchesTo;
  });

  const totalPages = Math.max(1, Math.ceil(filteredDraws.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const visibleDraws = filteredDraws.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  if (loading.activeLuckyDraw && !activeLuckyDraw) {
    return (
      <section className="lucky-draw-page">
        <div className="lucky-draw-shell">
          <div className="lucky-draw-table-empty">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="lucky-draw-page">
      <div className="lucky-draw-shell">
        <header className="lucky-draw-header">
          <div className="lucky-draw-heading">
            <span className="lucky-draw-heading-icon">
              <Trophy size={24} strokeWidth={2} />
            </span>
            <div>
              <span className="lucky-draw-kicker">MY STUFF</span>
              <h1>Lucky Draw</h1>
              <p>
                Participate in active lucky draws and stand a chance to win
                amazing prizes!
              </p>
            </div>
          </div>
          <span className="lucky-draw-count-chip">
            {filteredDraws.length} draws
          </span>
        </header>

        <div className="lucky-draw-toolbar">
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            aria-label="Lucky Draw entries per page"
          >
            <option value={5}>5 Entries</option>
            <option value={10}>10 Entries</option>
            <option value={20}>20 Entries</option>
          </select>

          <div className="lucky-draw-date-picker">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </div>

          <div className="lucky-draw-search">
            <Search size={18} strokeWidth={2} />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search Lucky Draw..."
              aria-label="Search Lucky Draw"
            />
            {searchValue && (
              <button
                type="button"
                onClick={() => setSearchValue("")}
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="lucky-draw-desktop-table-wrap">
          <table className="lucky-draw-desktop-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>DATE</th>
                <th>LUCKY DRAW DETAILS</th>
                <th>COUNTDOWN</th>
                <th>STATUS</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {visibleDraws.length > 0 ? (
                visibleDraws.map((draw, index) => {
                  const record = mapLuckyDrawToRecord(draw, index);
                  return (
                    <tr key={draw._id}>
                      <td className="lucky-draw-table-serial">
                        {record.serialNo}
                      </td>
                      <td className="lucky-draw-table-date">
                        <strong>{formatStuffDate(record.date)}</strong>
                        <small>{record.time}</small>
                      </td>
                      <td>
                        <div className="lucky-draw-table-product">
                          {renderDrawMedia(
                            draw,
                            "lucky-draw-table-product-image",
                          )}
                          <div>
                            <h2>{draw.name}</h2>
                            <p>{record.productDescription}</p>
                            <span>
                              <Users size={13} strokeWidth={2} />{" "}
                              {draw.analytics?.totalParticipants ?? 0}{" "}
                              participants
                            </span>
                            <strong>
                              Winner announcement:{" "}
                              {formatStuffLongDate(
                                draw.winnerAnnouncementDate,
                              )}
                            </strong>
                          </div>
                        </div>
                      </td>
                      <td>{renderTimer(draw._id)}</td>
                      <td className="lucky-draw-table-status">
                        <span className="lucky-draw-status lucky-draw-status-pending">
                          Active
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="lucky-draw-participate"
                          onClick={() => handleParticipate(draw._id)}
                          disabled={loading.participate}
                        >
                          {loading.participate
                            ? "Joining..."
                            : "Participate"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="lucky-draw-table-empty" colSpan={6}>
                    No active lucky draws found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="lucky-draw-mobile-list">
          {visibleDraws.length > 0 ? (
            visibleDraws.map((draw, index) => {
              const record = mapLuckyDrawToRecord(draw, index);
              return (
                <div className="lucky-draw-mobile-card" key={draw._id}>
                  <span className="lucky-draw-mobile-serial">
                    {record.serialNo}
                  </span>
                  {renderDrawMedia(draw, "lucky-draw-mobile-image")}
                  <div className="lucky-draw-mobile-copy">
                    <div className="lucky-draw-mobile-topline">
                      <span className="lucky-draw-mobile-status lucky-draw-mobile-status-pending">
                        Active
                      </span>
                      <span>
                        <CalendarDays size={13} strokeWidth={2} />
                        {formatStuffLongDate(
                          draw.luckyDrawDateRange?.endDate,
                        )}
                      </span>
                    </div>
                    <h2>{draw.name}</h2>
                    <p>{record.productDescription}</p>
                    {renderTimer(draw._id)}
                    <button
                      type="button"
                      className="lucky-draw-participate mobile"
                      onClick={() => handleParticipate(draw._id)}
                      disabled={loading.participate}
                    >
                      {loading.participate ? "Joining..." : "Participate"}
                    </button>
                  </div>
                  <ChevronRight
                    className="lucky-draw-mobile-chevron"
                    size={20}
                    strokeWidth={2}
                  />
                </div>
              );
            })
          ) : (
            <div className="lucky-draw-mobile-empty">
              <Trophy size={32} strokeWidth={1.8} />
              <strong>No lucky draws found</strong>
              <span>Check back soon for new draws.</span>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <footer className="lucky-draw-pagination">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setCurrentPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronRight
                size={18}
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
            <span>
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setCurrentPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
          </footer>
        )}
      </div>
    </section>
  );
};

export default LuckyDraw;
